// frontend/src/components/shared/StatusIcon.tsx
//
// The shared filled status icon (plan 03 section 4.2): a solid circle with a
// white glyph, in three sizes. Traditional icons, as PA asked: green fill with
// a white tick for done, blue with a white "!" for needs you, and so on. It
// replaces the outline lucide icons on step rows, decided lines, box titles,
// policy lines and chips. `label` becomes the accessible name; the word is
// always printed beside the icon by the caller (state is never colour alone).

import { cn } from '@/lib/utils'
import { STATUS_ICON, statusIconLabel, type StatusGlyph, type StatusIconKind, type StatusIconSize } from './status-icon'

export type { StatusIconKind, StatusIconSize } from './status-icon'

function Glyph({ glyph }: { glyph: StatusGlyph }) {
  // Drawn on a 16 x 16 box, white, so the glyph scales with the circle.
  const common = { fill: 'none', stroke: '#FFFFFF', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }
  switch (glyph) {
    case 'tick':
      return <path d='M4.5 8.3l2.3 2.3 4.7-5' {...common} />
    case 'bang':
      return (
        <>
          <path d='M8 4.2v4.9' {...common} />
          <circle cx='8' cy='11.6' r='1.1' fill='#FFFFFF' />
        </>
      )
    case 'dot':
      return <circle cx='8' cy='8' r='2.6' fill='#FFFFFF' />
    case 'clock':
      return (
        <>
          <circle cx='8' cy='8' r='4.4' {...common} strokeWidth={1.6} />
          <path d='M8 5.6V8l1.6 1.2' {...common} strokeWidth={1.6} />
        </>
      )
    case 'cross':
      return <path d='M5.4 5.4l5.2 5.2M10.6 5.4l-5.2 5.2' {...common} />
    case 'dash':
      return <path d='M4.8 8h6.4' {...common} />
    case 'return':
      return <path d='M6.2 4.8L3.8 7.2l2.4 2.4M4 7.2h5.2a2.8 2.8 0 010 5.6H7.6' {...common} strokeWidth={1.6} />
    case 'padlock':
      return (
        <>
          <rect x='4.6' y='7.4' width='6.8' height='5' rx='1' fill='#FFFFFF' />
          <path d='M6 7.4V6a2 2 0 014 0v1.4' {...common} strokeWidth={1.5} />
        </>
      )
  }
}

export function StatusIcon({
  kind,
  size = 16,
  label,
  className,
  decorative = false,
}: {
  kind: StatusIconKind
  size?: StatusIconSize
  /** The accessible name; defaults to the kind's word ("Done", "Needs your decision"). */
  label?: string
  className?: string
  /** True when the same word is printed beside the icon and read already. */
  decorative?: boolean
}) {
  const spec = STATUS_ICON[kind]
  const name = statusIconLabel(kind, label)
  return (
    <span
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : name}
      aria-hidden={decorative ? true : undefined}
      data-status-icon={kind}
      className={cn('relative inline-flex shrink-0 items-center justify-center rounded-full', spec.fillClass, className)}
      style={{ width: size, height: size }}
    >
      {kind === 'working' && (
        <span
          aria-hidden='true'
          className='absolute inset-0 rounded-full bg-[#4C6FD1]/40 motion-safe:animate-ping'
          style={{ animationDuration: '1.2s' }}
        />
      )}
      <svg viewBox='0 0 16 16' width={size * 0.8} height={size * 0.8} aria-hidden='true' className='relative'>
        <Glyph glyph={spec.glyph} />
      </svg>
    </span>
  )
}

/** An icon with its word beside it: the form every status takes on the page. */
export function StatusWord({
  kind,
  children,
  size = 16,
  className,
  wordClassName,
}: {
  kind: StatusIconKind
  children?: React.ReactNode
  size?: StatusIconSize
  className?: string
  wordClassName?: string
}) {
  const word = children ?? STATUS_ICON[kind].word
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <StatusIcon kind={kind} size={size} decorative />
      <span className={cn('text-sm font-semibold', STATUS_ICON[kind].textClass, wordClassName)}>{word}</span>
    </span>
  )
}

/** The same component under the name plan 02 uses. */
export { StatusIcon as StatusMark }
